import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { getImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Bell, Menu, MessageSquare, Users, Calendar, Compass, Home, Settings, LogOut, User, Zap, Crown, Building2, Shield, Search, MapPin, Briefcase, ArrowRight, ShieldCheck, GraduationCap, LayoutDashboard } from "lucide-react";

const ROLE_COLORS = {
  athlete: { color: "#074572", bg: "#EFF6FF", label: "Athlete" },
  coach: { color: "#F85037", bg: "#FEF2F2", label: "Coach" },
  scout: { color: "#10B981", bg: "#ECFDF5", label: "Scout" },
  club: { color: "#8B5CF6", bg: "#F5F3FF", label: "Club" },
};
const EXCLUDED_ROLES = ["admin", "super_admin", "moderator", "verification_admin", "user", "parent"];
const DASHBOARD_ROLES = ["scout", "coach", "club", "explorer", "professional", "admin", "super_admin", "moderator", "verification_admin"];

const navLinks = [
  { to: "/feed", label: "Feed", icon: Home },
  { to: "/connections", label: "Network", icon: Users },
  { to: "/scout", label: "Scout Navigator", icon: Compass },
  { to: "/messages", label: "Messages", icon: MessageSquare },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [proEnabled, setProEnabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [notifRes, settingsRes, msgRes] = await Promise.all([
          api.get("/notifications/unread-count"),
          api.get("/platform/settings"),
          api.get("/messages/unread-total").catch(() => ({ data: { conversations: 0 } })),
        ]);
        setUnreadCount(notifRes.data.count);
        setProEnabled(settingsRes.data.pro_enabled || false);
        setUnreadMessages(msgRes.data?.conversations || 0);
      } catch {}
    };
    fetchData();
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        setUnreadCount(data.count);
        const msg = await api.get("/messages/unread-total").catch(() => ({ data: { conversations: 0 } }));
        setUnreadMessages(msg.data?.conversations || 0);
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  const avatarUrl = getImageUrl(user?.profile_image);

  const [searchQuery, setSearchQuery] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPeople, setPreviewPeople] = useState([]);
  const [previewTeams, setPreviewTeams] = useState([]);
  const searchContainerRef = useRef(null);

  // Debounced fetch of live preview
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) { setPreviewPeople([]); setPreviewTeams([]); return; }
    let active = true;
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const [peopleRes, teamsRes] = await Promise.all([
          api.get(`/scout/discover?search=${encodeURIComponent(q)}&limit=10`),
          api.get(`/teams?search=${encodeURIComponent(q)}`),
        ]);
        if (!active) return;
        const filtered = (peopleRes.data.users || []).filter(u => !EXCLUDED_ROLES.includes(u.role)).slice(0, 3);
        setPreviewPeople(filtered);
        setPreviewTeams((teamsRes.data || []).slice(0, 2));
      } catch {}
      finally { if (active) setPreviewLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(handle); };
  }, [searchQuery]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setPreviewOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const closePreview = () => {
    setPreviewOpen(false);
    setSearchQuery("");
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    closePreview();
  };

  const hasPreview = searchQuery.trim().length > 0 && previewOpen;
  const hasResults = previewPeople.length > 0 || previewTeams.length > 0;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E6E6E6]" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/feed" className="flex items-center gap-2" data-testid="navbar-logo">
          <div className="w-9 h-9 rounded-md bg-white border border-[#E6E6E6] flex items-center justify-center p-1">
            <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
          </div>
          <span className="text-lg font-semibold text-[#074572] hidden sm:block" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
        </Link>

        <form onSubmit={handleSearchSubmit} className="hidden md:flex items-center flex-1 max-w-md mx-4" data-testid="navbar-search-form">
          <div ref={searchContainerRef} className="relative w-full">
            <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPreviewOpen(true); }}
              onFocus={() => setPreviewOpen(true)}
              placeholder="Search players, scouts, coaches..."
              aria-label="Search SportsUnity"
              className="w-full h-9 pl-9 pr-3 rounded-full bg-zinc-100 hover:bg-zinc-100/80 focus:bg-white border border-transparent focus:border-[#074572]/30 text-sm text-zinc-700 placeholder:text-zinc-400 outline-none transition-colors"
              data-testid="navbar-search-input"
            />

            {hasPreview && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-[#E6E6E6] rounded-lg shadow-lg overflow-hidden z-50" data-testid="navbar-search-preview">
                {previewLoading && !hasResults ? (
                  <div className="px-4 py-6 text-center text-xs text-zinc-400">Searching...</div>
                ) : !hasResults ? (
                  <div className="px-4 py-6 text-center text-xs text-zinc-400" data-testid="navbar-search-empty">No results for "{searchQuery.trim()}"</div>
                ) : (
                  <>
                    {previewPeople.length > 0 && (
                      <div className="py-1">
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">People</div>
                        {previewPeople.map(u => {
                          const meta = ROLE_COLORS[u.role] || { color: "#074572", bg: "#EFF6FF", label: u.role };
                          const userId = u._id || u.id;
                          const initials = u.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
                          const avatarUrl = getImageUrl(u.profile_image);
                          return (
                            <button
                              key={userId}
                              type="button"
                              onClick={() => { closePreview(); navigate(`/profile/${userId}`); }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 text-left"
                              data-testid={`preview-person-${userId}`}
                            >
                              <Avatar className="h-8 w-8 shrink-0">
                                {avatarUrl ? <AvatarImage src={avatarUrl} alt={u.name} /> : null}
                                <AvatarFallback className="bg-[#074572] text-white text-[10px] font-medium">{initials}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-sm font-medium text-zinc-900 truncate">{u.name}</p>
                                  {u.is_premium && <Crown size={10} className="text-amber-500 shrink-0" />}
                                  {u.ergo_verified && <ShieldCheck size={10} className="text-[#074572] shrink-0" />}
                                  {u.coaching_license && <GraduationCap size={10} className="text-[#F85037] shrink-0" />}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-zinc-500">
                                  <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold uppercase tracking-wider shrink-0" style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
                                  {u.position && <span className="truncate">{u.position}</span>}
                                  {u.location && <span className="flex items-center gap-0.5 truncate"><MapPin size={9} />{u.location}</span>}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {previewTeams.length > 0 && (
                      <div className="py-1 border-t border-[#E6E6E6]">
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Teams & Clubs</div>
                        {previewTeams.map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => { closePreview(); navigate(`/teams/${t.id}`); }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 text-left"
                            data-testid={`preview-team-${t.id}`}
                          >
                            <div className="w-8 h-8 rounded-md bg-[#074572]/5 flex items-center justify-center shrink-0">
                              <Building2 size={14} className="text-[#074572]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-zinc-900 truncate">{t.name}</p>
                              <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-zinc-500">
                                {t.team_type && <span className="capitalize">{t.team_type}</span>}
                                {t.location && <span className="flex items-center gap-0.5 truncate"><MapPin size={9} />{t.location}</span>}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <button
                  type="submit"
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#074572]/5 hover:bg-[#074572]/10 text-xs font-medium text-[#074572] border-t border-[#E6E6E6] transition-colors"
                  data-testid="preview-view-all"
                >
                  <span>View all results for "{searchQuery.trim()}"</span>
                  <ArrowRight size={12} />
                </button>
              </div>
            )}
          </div>
        </form>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.filter(l => !l.roles || l.roles.includes(user?.role)).map(link => {
            const Icon = link.icon;
            const active = location.pathname === link.to || location.pathname.startsWith(link.to + "/");
            const badgeCount = link.to === "/messages" ? unreadMessages : 0;
            return (
              <Link key={link.to} to={link.to} data-testid={`nav-${link.label.toLowerCase()}`}
                className={`relative flex items-center gap-1.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors ${
                  active ? "text-[#074572] bg-[#074572]/5" : "text-zinc-500 hover:text-[#074572] hover:bg-zinc-50"
                }`}>
                <Icon size={16} strokeWidth={1.5} />
                {link.label}
                {badgeCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#F85037] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    data-testid="nav-messages-badge"
                  >
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="relative" onClick={() => navigate("/notifications")} data-testid="nav-notifications-btn">
            <Bell size={20} strokeWidth={1.5} className="text-zinc-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#F85037] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Button>
          {proEnabled && !user?.is_premium && (
            <Link to="/premium">
              <Button size="sm" className="bg-[#F85037] hover:bg-[#e04530] text-white text-xs gap-1 hidden sm:flex" data-testid="nav-premium-btn">
                <Crown size={12} /> Pro
              </Button>
            </Link>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="nav-user-menu">
                <Avatar className="h-8 w-8 border-2 border-[#E6E6E6]">
                  {avatarUrl ? <AvatarImage src={avatarUrl} alt={user?.name} /> : null}
                  <AvatarFallback className="bg-[#074572] text-white text-xs font-medium">{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium text-zinc-700">{user?.name?.split(" ")[0]}</span>
                {user?.is_premium && <Crown size={12} className="text-amber-500" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User size={16} className="mr-2" /> Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/team")} data-testid="menu-team"><Building2 size={16} className="mr-2" /> Team</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/spotlight")} data-testid="menu-spotlight"><Zap size={16} className="mr-2" /> Spotlight</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/connections")} data-testid="menu-network"><Users size={16} className="mr-2" /> Network</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/events")} data-testid="menu-events"><Calendar size={16} className="mr-2" /> Events</DropdownMenuItem>
              {DASHBOARD_ROLES.includes(user?.role) && (
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard size={16} className="mr-2" /> Scout Dashboard</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {proEnabled && <DropdownMenuItem onClick={() => navigate("/premium")} data-testid="menu-premium"><Crown size={16} className="mr-2" /> Premium</DropdownMenuItem>}
              {["super_admin","admin","moderator","verification_admin"].includes(user?.role) && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield size={16} className="mr-2" /> Admin Panel</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings"><Settings size={16} className="mr-2" /> Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout"><LogOut size={16} className="mr-2" /> Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu size={20} /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 p-0">
              <div className="p-6 border-b border-[#E6E6E6]">
                <p className="font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>{user?.name}</p>
                <p className="text-sm text-zinc-500 capitalize">{user?.role}</p>
              </div>
              <nav className="p-4 space-y-1">
                {navLinks.filter(l => !l.roles || l.roles.includes(user?.role)).map(link => {
                  const Icon = link.icon;
                  const active = location.pathname === link.to;
                  return (
                    <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                        active ? "text-[#074572] bg-[#074572]/5" : "text-zinc-600 hover:bg-zinc-50"
                      }`}>
                      <Icon size={18} strokeWidth={1.5} /> {link.label}
                    </Link>
                  );
                })}
                {proEnabled && (
                  <Link to="/premium" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#F85037]">
                    <Crown size={18} strokeWidth={1.5} /> Premium
                  </Link>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
